import { useState, useRef, useEffect } from 'react';

export default function FilterBar({ filters, filterLabels, filterState, onFilterChange, onClearAll }) {
  // filters: { "Education": [{value: "College graduate", count: 140}, ...], "Income": [...], ... }
  // Also includes standard demographic filters
  const [openDropdown, setOpenDropdown] = useState(null);
  const barRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const totalSelected = Object.values(filterState).reduce((sum, arr) => sum + arr.length, 0);

  const displayValue = (label) => {
    const sel = filterState[label] || [];
    if (sel.length === 0) return 'All';
    if (sel.length === 1) return sel[0];
    return `${sel[0]} +${sel.length - 1}`;
  };

  const handleOptionClick = (label, value) => {
    const current = filterState[label] || [];
    let next;
    if (value === 'All') {
      next = [];
    } else {
      const idx = current.indexOf(value);
      if (idx >= 0) {
        next = current.filter((_, i) => i !== idx);
      } else {
        next = [...current, value];
      }
    }
    onFilterChange(label, next);
  };

  return (
    <>
      <div ref={barRef} style={barStyle}>
        {Object.entries(filters).map(([label, options]) => {
          const isActive = (filterState[label] || []).length > 0;
          const isOpen = openDropdown === label;

          return (
            <div key={label} style={{ position: 'relative' }}>
              <div
                style={{
                  ...chipStyle,
                  ...(isActive ? activeChipStyle : {})
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(isOpen ? null : label);
                }}
              >
                <span style={labelStyle}>{filterLabels?.[label] || label}</span>
                <span style={valueStyle}>{displayValue(label)}</span>
                <span style={arrowStyle}>&#9662;</span>
              </div>

              {isOpen && (
                <div style={dropdownStyle}>
                  <div
                    style={{
                      ...optionStyle,
                      ...allOptionStyle,
                      ...(!isActive ? selectedOptionStyle : {})
                    }}
                    onClick={(e) => { e.stopPropagation(); handleOptionClick(label, 'All'); }}
                  >
                    <div style={{
                      ...checkboxStyle,
                      ...(!isActive ? checkedStyle : {})
                    }}>
                      {!isActive && <span style={checkmarkStyle}></span>}
                    </div>
                    All
                  </div>
                  {options.map(opt => {
                    const val = typeof opt === 'string' ? opt : opt.value;
                    const isSelected = (filterState[label] || []).includes(val);
                    return (
                      <div
                        key={val}
                        style={{
                          ...optionStyle,
                          ...(isSelected ? selectedOptionStyle : {})
                        }}
                        onClick={(e) => { e.stopPropagation(); handleOptionClick(label, val); }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f7f8fa'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{
                          ...checkboxStyle,
                          ...(isSelected ? checkedStyle : {})
                        }}>
                          {isSelected && <span style={checkmarkStyle}></span>}
                        </div>
                        {val}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {totalSelected > 0 && (
          <button style={clearBtnStyle} onClick={onClearAll}>
            Clear {totalSelected} filter{totalSelected !== 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Active filter tags */}
      {totalSelected > 0 && (
        <div style={activeBarStyle}>
          <span style={activeBarLabelStyle}>Active Filters:</span>
          {Object.entries(filterState).map(([label, values]) =>
            values.map(val => (
              <span key={`${label}-${val}`} style={tagStyle}>
                <span style={tagLabelStyle}>{filterLabels?.[label] || label}</span>
                {val}
                <span
                  style={removeTagStyle}
                  onClick={() => handleOptionClick(label, val)}
                  onMouseEnter={(e) => e.target.style.color = '#e07a5f'}
                  onMouseLeave={(e) => e.target.style.color = '#8a94a3'}
                >
                  &times;
                </span>
              </span>
            ))
          )}
        </div>
      )}
    </>
  );
}

// Styles
const barStyle = {
  padding: '14px 28px', background: '#fff', borderBottom: '1px solid #e2e6ea',
  display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'
};

const chipStyle = {
  display: 'flex', alignItems: 'center', gap: '6px',
  padding: '8px 14px', borderRadius: '8px', border: '1px solid #e2e6ea',
  background: '#fff', fontSize: '13px', fontWeight: 500,
  cursor: 'pointer', transition: 'all 0.15s ease'
};

const activeChipStyle = { border: '2px solid #2ec4b6', background: 'rgba(46,196,182,0.05)' };
const labelStyle = { color: '#5a6577', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' };
const valueStyle = { fontWeight: 600, color: '#1a2332' };
const arrowStyle = { fontSize: '10px', color: '#8a94a3' };

const dropdownStyle = {
  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 60,
  background: '#fff', borderRadius: '10px', padding: '4px', minWidth: '160px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid #e2e6ea'
};

const optionStyle = {
  padding: '8px 12px', borderRadius: '6px', cursor: 'pointer',
  fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px'
};

const allOptionStyle = {
  borderBottom: '1px solid #e2e6ea', marginBottom: '4px', paddingBottom: '10px'
};

const selectedOptionStyle = {
  fontWeight: 600, background: 'rgba(46,196,182,0.08)', color: '#0f4c5c'
};

const checkboxStyle = {
  width: '16px', height: '16px', borderRadius: '4px',
  border: '2px solid #ccd0d5', flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center'
};

const checkedStyle = { background: '#2ec4b6', borderColor: '#2ec4b6' };

const checkmarkStyle = {
  display: 'block', width: '5px', height: '9px',
  borderRight: '2px solid #fff', borderBottom: '2px solid #fff',
  transform: 'rotate(45deg)', marginTop: '-1px'
};

const clearBtnStyle = {
  padding: '8px 14px', borderRadius: '8px', border: 'none',
  background: 'rgba(224,122,95,0.08)', color: '#e07a5f',
  fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
};

const activeBarStyle = {
  padding: '8px 28px', background: '#fff', borderBottom: '1px solid #e2e6ea',
  display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'
};

const activeBarLabelStyle = {
  fontSize: '11px', color: '#8a94a3', textTransform: 'uppercase',
  letterSpacing: '0.5px', fontWeight: 600, marginRight: '4px'
};

const tagStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '4px 10px', borderRadius: '6px',
  background: 'rgba(46,196,182,0.08)', border: '1px solid rgba(46,196,182,0.2)',
  fontSize: '12px', color: '#0f4c5c', fontWeight: 500
};

const tagLabelStyle = {
  color: '#8a94a3', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px'
};

const removeTagStyle = {
  cursor: 'pointer', color: '#8a94a3', fontSize: '14px', lineHeight: 1, marginLeft: '2px'
};
